import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { apiErrorText, Modal } from "./adminUi.js";

/** Admin · signup requests (0069) — the free-plan approval queue. Paid requests auto-approve on
 *  payment and show here only for visibility; only pending_approval rows are actionable. */

interface SignupRequestRow {
  id: string;
  orgId: string;
  orgName: string;
  plan: "free" | "paid";
  /** the plan picked at signup — null for free requests (and pre-plans paid requests) */
  planName?: string | null;
  planQuantity?: number | null;
  status: string;
  requestedByName: string;
  requestedByEmail: string;
  createdAt: string;
  reviewedAt: string | null;
}

interface ListResponse {
  requests: SignupRequestRow[];
  stats?: {
    today: number;
    waiting: number;
    approved: number;
    rejected: number;
  };
}

/** "Free plan" or "<plan> × <qty>" — the signup's own choice, never a credits figure. */
function planText(r: SignupRequestRow): string {
  if (r.plan === "free") return "Free plan";
  if (r.planName) return `${r.planName} × ${r.planQuantity ?? 1}`;
  return "Paid plan";
}

const STATUS_LABEL: Record<string, string> = {
  pending_verification: "Awaiting email verification",
  pending_approval: "Awaiting approval",
  pending_payment: "Awaiting payment",
  approved: "Approved",
  rejected: "Rejected",
};

export function SignupRequests(): ReactNode {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Approve / reject both confirm first — each sends the applicant an email and can't be undone here.
  const [confirm, setConfirm] = useState<{ row: SignupRequestRow; action: "approve" | "reject" } | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-signup-requests"],
    queryFn: () => api.get<ListResponse>("/admin/signup-requests"),
  });

  const refresh = (): void => void qc.invalidateQueries({ queryKey: ["admin-signup-requests"] });

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/admin/signup-requests/${id}/approve`),
    onSuccess: refresh,
  });
  const reject = useMutation({
    mutationFn: (id: string) => api.post(`/admin/signup-requests/${id}/reject`),
    onSuccess: refresh,
  });

  const act = (id: string, m: typeof approve): void => {
    setErr(null);
    setBusyId(id);
    m.mutate(id, {
      onError: (e) => setErr(apiErrorText(e, "That action didn't go through. Please retry.")),
      onSettled: () => {
        setBusyId(null);
        setConfirm(null);
      },
    });
  };

  const requests = data?.requests ?? [];

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Signup requests</h1>
          <div className="sub">Self-serve signups — free plan needs a look; paid auto-approves on payment.</div>
        </div>
      </div>

      {data?.stats && (
        <div className="grid g4" style={{ marginBottom: 16 }}>
          <div className="card pad stat">
            <div className="l">Today's requests</div>
            <div className="n tnum">{data.stats.today.toLocaleString("en-IN")}</div>
            <div className="d">created today</div>
          </div>
          <div className="card pad stat">
            <div className="l">Awaiting approval</div>
            <div className="n violet tnum">{data.stats.waiting.toLocaleString("en-IN")}</div>
            <div className="d">free plans to review</div>
          </div>
          <div className="card pad stat">
            <div className="l">Approved</div>
            <div className="n teal tnum">{data.stats.approved.toLocaleString("en-IN")}</div>
            <div className="d">total approved</div>
          </div>
          <div className="card pad stat">
            <div className="l">Rejected</div>
            <div className="n tnum">{data.stats.rejected.toLocaleString("en-IN")}</div>
            <div className="d">total rejected</div>
          </div>
        </div>
      )}

      {isLoading && <div className="card pad mut sm">Loading signup requests…</div>}

      {isError && (
        <div className="banner crit">
          <span>{apiErrorText(error, "Couldn't load signup requests. Please retry in a moment.")}</span>
        </div>
      )}

      {err && (
        <div className="banner crit" style={{ marginBottom: 12 }}>
          <span>{err}</span>
        </div>
      )}

      {!isLoading && !isError && requests.length === 0 && (
        <div className="card pad mut sm">No signup requests yet.</div>
      )}

      {!isLoading && !isError && requests.length > 0 && (
        <div className="card">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Requested by</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.orgName}</td>
                    <td>
                      {r.requestedByName}
                      <div className="mut sm" style={{ fontWeight: 400 }}>{r.requestedByEmail}</div>
                    </td>
                    <td>
                      <span className={`pill ${r.plan === "free" ? "n" : "t"}`}>{planText(r)}</span>
                    </td>
                    <td>
                      <span className={r.status === "pending_approval" ? "pill r" : "mut sm"}>
                        {STATUS_LABEL[r.status] ?? r.status}
                      </span>
                    </td>
                    <td className="mut sm">{new Date(r.createdAt).toLocaleString("en-IN")}</td>
                    <td>
                      {r.status === "pending_approval" && (
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button
                            className="btn sm ghost"
                            disabled={busyId === r.id}
                            onClick={() => setConfirm({ row: r, action: "reject" })}
                          >
                            Reject
                          </button>
                          <button
                            className="btn sm primary"
                            disabled={busyId === r.id}
                            onClick={() => setConfirm({ row: r, action: "approve" })}
                          >
                            Approve
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {confirm && (() => {
        const { row, action } = confirm;
        const busy = busyId === row.id;
        const isReject = action === "reject";
        return (
          <Modal
            title={isReject ? `Reject ${row.orgName}?` : `Approve ${row.orgName}?`}
            onClose={() => !busy && setConfirm(null)}
            footer={
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="btn sm ghost" disabled={busy} onClick={() => setConfirm(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn sm ${isReject ? "danger" : "primary"}`}
                  disabled={busy}
                  onClick={() => act(row.id, isReject ? reject : approve)}
                >
                  {isReject ? (busy ? "Rejecting…" : "Reject request") : busy ? "Approving…" : "Approve request"}
                </button>
              </div>
            }
          >
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
              {isReject ? (
                <>
                  {row.requestedByName} ({row.requestedByEmail}) won&rsquo;t get access to TalentHop. They&rsquo;ll get a
                  polite email letting them know the request wasn&rsquo;t approved.
                </>
              ) : (
                <>
                  {row.orgName} gets the <b>{planText(row)}</b> and {row.requestedByName} ({row.requestedByEmail}) is
                  emailed that they can sign in.
                </>
              )}
            </p>
            {err && (
              <div className="banner crit" style={{ marginTop: 12 }}>
                <span>{err}</span>
              </div>
            )}
          </Modal>
        );
      })()}
    </section>
  );
}
